import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowDownToLine,
  CheckCircle2,
  XCircle,
  Clock,
  X,
  Loader2,
  Filter,
} from "lucide-react";
import { trustApi, clientsApi, mattersApi, PaginatedResponse } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import { notifyError, notifySuccess } from "../ui/Toast";

interface Props {
  bankAccountId: string;
  onRefresh: () => void;
}

interface Receipt {
  _id: string;
  clientId: { _id: string; displayName: string } | string;
  matterId: { _id: string; title: string } | string;
  amountCents: number;
  currency: string;
  method: string;
  payer: { name: string; kind: string };
  reference: string | null;
  status: string;
  receivedAt: string;
  clearing: { status: string; clearedAt: string | null };
}

const statusConfig: Record<string, { label: string; bg: string; text: string; Icon: React.FC<any> }> = {
  pending: { label: "Pending", bg: "bg-amber-100", text: "text-amber-700", Icon: Clock },
  cleared: { label: "Cleared", bg: "bg-emerald-100", text: "text-emerald-700", Icon: CheckCircle2 },
  void: { label: "Void", bg: "bg-red-100", text: "text-red-700", Icon: XCircle },
};

const methodLabels: Record<string, string> = {
  wire: "Wire",
  cheque: "Cheque",
  eft: "EFT",
  card: "Card",
  cash: "Cash",
};

function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

const TrustReceiptsTab: React.FC<Props> = ({ bankAccountId, onRefresh }) => {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Create form state
  const [clients, setClients] = useState<any[]>([]);
  const [matters, setMatters] = useState<any[]>([]);
  const [form, setForm] = useState({
    clientId: "",
    matterId: "",
    amountCents: "",
    method: "eft" as string,
    payerName: "",
    payerKind: "client" as string,
    reference: "",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const fetchReceipts = useCallback(async () => {
    if (!bankAccountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, any> = {
        bankAccountId,
        page: pagination.page,
        limit: pagination.limit,
      };
      if (statusFilter) params.status = statusFilter;

      const res = await trustApi.listReceipts(params);
      const data = res.data as PaginatedResponse<Receipt>;
      setReceipts(data.data || []);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch receipts:", err);
    } finally {
      setLoading(false);
    }
  }, [bankAccountId, pagination.page, pagination.limit, statusFilter]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  // Load clients for create modal
  useEffect(() => {
    if (showCreateModal) {
      clientsApi.list({ limit: 100 }).then((res) => setClients(res.data?.data || []));
    }
  }, [showCreateModal]);

  // Load matters when client changes
  useEffect(() => {
    if (form.clientId) {
      mattersApi.list({ clientId: form.clientId, limit: 100 }).then((res) => setMatters(res.data?.data || []));
    } else {
      setMatters([]);
    }
  }, [form.clientId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError("");
    try {
      const amountCents = Math.round(parseFloat(form.amountCents) * 100);
      if (isNaN(amountCents) || amountCents <= 0) {
        setCreateError("Please enter a valid amount");
        setCreating(false);
        return;
      }

      await trustApi.createReceipt({
        bankAccountId,
        clientId: form.clientId,
        matterId: form.matterId,
        amountCents,
        method: form.method,
        payer: {
          kind: form.payerKind,
          name: form.payerName,
          clientId: form.payerKind === "client" ? form.clientId : undefined,
        },
        reference: form.reference || undefined,
      });

      setShowCreateModal(false);
      setForm({
        clientId: "",
        matterId: "",
        amountCents: "",
        method: "eft",
        payerName: "",
        payerKind: "client",
        reference: "",
      });
      fetchReceipts();
      onRefresh();
    } catch (err: any) {
      setCreateError(err.response?.data?.error || "Failed to create receipt");
    } finally {
      setCreating(false);
    }
  };

  const handleClear = async (id: string) => {
    setActionLoading(id);
    try {
      await trustApi.clearReceipt(id);
      fetchReceipts();
      onRefresh();
      notifySuccess("Receipt marked as cleared.");
    } catch (err) {
      notifyError("Couldn't clear the receipt.", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleVoid = async (id: string) => {
    const reason = window.prompt("Reason for voiding this receipt:");
    if (!reason) return;
    setActionLoading(id);
    try {
      await trustApi.voidReceipt(id, reason);
      fetchReceipts();
      onRefresh();
      notifySuccess("Receipt voided.");
    } catch (err) {
      notifyError("Couldn't void the receipt.", err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <SearchableSelect
            options={[
              { value: "pending", label: "Pending" },
              { value: "cleared", label: "Cleared" },
              { value: "void", label: "Void" },
            ]}
            value={statusFilter || null}
            onChange={(val) => {
              setStatusFilter(val || "");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="All Statuses"
            icon={Filter}
            clearable
          />
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={!bankAccountId}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <Plus className="h-4 w-4" />
          New Deposit
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : receipts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <ArrowDownToLine className="h-12 w-12 mb-3 text-slate-300" />
            <p className="font-medium text-lg">No trust receipts</p>
            <p className="text-sm mt-1">Deposit funds into trust to get started</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Date</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Client</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">Payer</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">Method</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((receipt, i) => {
                    const clientName = typeof receipt.clientId === "object" && receipt.clientId ? receipt.clientId.displayName : "Unknown";
                    const status = statusConfig[receipt.status] || statusConfig.pending;
                    const StatusIcon = status.Icon;

                    return (
                      <motion.tr
                        key={receipt._id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.02 }}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-slate-600">{formatDate(receipt.receivedAt)}</td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-800">{clientName}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden md:table-cell">{receipt.payer?.name || "-"}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden lg:table-cell">{methodLabels[receipt.method] || receipt.method}</td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-slate-800">{formatCents(receipt.amountCents)}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                            <StatusIcon className="h-3 w-3" />
                            {status.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            {receipt.status === "pending" && (
                              <button
                                onClick={() => handleClear(receipt._id)}
                                disabled={actionLoading === receipt._id}
                                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                              >
                                {actionLoading === receipt._id ? "..." : "Clear"}
                              </button>
                            )}
                            {receipt.status !== "void" && (
                              <button
                                onClick={() => handleVoid(receipt._id)}
                                disabled={actionLoading === receipt._id}
                                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                              >
                                Void
                              </button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Receipt Modal */}
      <PremiumModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="New Trust Deposit"
        icon={ArrowDownToLine}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="createReceiptForm"
              disabled={creating || !form.clientId || !form.matterId}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              {creating ? "Creating..." : "Create Deposit"}
            </button>
          </div>
        }
      >
        {createError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{createError}</div>
        )}

        <form id="createReceiptForm" onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
            <SearchableSelect
              options={clients.map((c: any) => ({
                value: c._id,
                label: c.displayName,
              }))}
              value={form.clientId || null}
              onChange={(val) => setForm({ ...form, clientId: val || "", matterId: "" })}
              placeholder="Select client..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Matter *</label>
            <SearchableSelect
              options={matters.map((m: any) => ({
                value: m._id,
                label: m.title,
              }))}
              value={form.matterId || null}
              onChange={(val) => setForm({ ...form, matterId: val || "" })}
              placeholder="Select matter..."
              required
              disabled={!form.clientId}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Amount (CAD) *</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              value={form.amountCents}
              onChange={(e) => setForm({ ...form, amountCents: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Payment Method *</label>
            <SearchableSelect
              options={[
                { value: "eft", label: "EFT" },
                { value: "wire", label: "Wire Transfer" },
                { value: "cheque", label: "Cheque" },
                { value: "card", label: "Card" },
                { value: "cash", label: "Cash" },
              ]}
              value={form.method}
              onChange={(val) => setForm({ ...form, method: val || "eft" })}
              placeholder="Select method"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Payer Name *</label>
            <input
              type="text"
              required
              value={form.payerName}
              onChange={(e) => setForm({ ...form, payerName: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="Name of the payer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Payer Type</label>
            <SearchableSelect
              options={[
                { value: "client", label: "Client" },
                { value: "contact", label: "Contact" },
                { value: "external", label: "External" },
              ]}
              value={form.payerKind}
              onChange={(val) => setForm({ ...form, payerKind: val || "client" })}
              placeholder="Select payer type"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reference</label>
            <input
              type="text"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="Cheque #, wire ref, etc."
            />
          </div>
        </form>
      </PremiumModal>
    </div>
  );
};

export default TrustReceiptsTab;
